/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren } from 'preact';
import { rowAxisStyles, rowBackgroundStyles } from './themes/GanttStyles.css';
import { classNames } from '../utils/UNSAFE_classNames';
import type { Gantt as GanttProps, GanttRow, GanttTask } from './Gantt.types';
import { getRowLabelAriaLabel } from './utils/ariaUtils';
import { GanttRowLayoutObj } from './utils/dataLayoutUtils';

type Props<K1, K2, D1 extends GanttRow<K1, D1, D2>, D2 extends GanttTask<K2>> = {
  rowObj: GanttRowLayoutObj<K1, K2>;
  rowData: GanttProps<K2, D1, D2>['rows'];
  isRowSelected: boolean;
  isFocused: boolean;
  isHorizontalGridlinesVisible: boolean;
  ariaActive?: { dataId: K1; ariaId: string; type: 'rowLabel' };
  children: ComponentChildren;
};

export function GanttRowLabelContainer<
  K1,
  K2,
  D1 extends GanttRow<K1, D1, D2>,
  D2 extends GanttTask<K2>
>({
  rowObj,
  rowData,
  isRowSelected,
  isFocused,
  isHorizontalGridlinesVisible,
  ariaActive,
  children
}: Props<K1, K2, D1, D2>) {
  const ariaLabel = getRowLabelAriaLabel(rowObj, rowData);
  return (
    <div
      id={ariaActive?.dataId === rowObj.id ? ariaActive.ariaId : undefined}
      key={rowObj.id}
      data-oj-row-index={rowObj.index}
      role="row"
      aria-label={ariaLabel}
      class={classNames([
        rowAxisStyles.labelContainer,
        isRowSelected && rowBackgroundStyles.selected,
        isFocused && rowAxisStyles.labelContainerFocused,
        isHorizontalGridlinesVisible && rowAxisStyles.withLabelSeparatorBottom
      ])}>
      <div role="rowheader">{children}</div>
    </div>
  );
}
